import { useState, useEffect } from 'react'
import { Formik, Form } from 'formik'
import { Grid2 } from '@mui/material'
import { Field } from 'formik'
import { Button } from '@mui/material'
import * as Yup from 'yup'
import { TextFormField } from '../../../shared/components/formfields/TextFormField'
import { useAdminCountry, useAdminState } from '../../../assets/api/useAdminApi'
import { IdAutoCompleteField } from '../../../shared/components/formfields/IdAutoCompleteField'
import { Grid_Gap } from '../../../shared/constants'
import { useToaster } from '../../../shared/utils/ToasterProvider'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../../../assets/nodeApi/axiosInstance'
import CustomDivider from '../../../shared/utils/CustomDivider'

const CityForm = ({ initialCity = null, isEditing = false, onSuccess = () => { } }) => {
    
    const [searchState, setSearchState] = useState("")
    const [countryId, setCountryId] = useState(initialCity?.country_id || null);
    const { data: stateList, isLoading: stateLoading } = useAdminState({ country_id: countryId, search: searchState });
    const { data: countryList, isLoading: countryLoading } = useAdminCountry();
    const { showToaster } = useToaster();
    const queryClient = useQueryClient();

    const countryOption = countryList?.data?.map((item) => ({
        id: item?.id,
        label: item?.country_name,
        value: item?.country_name,
    })) || [];

    const stateOption = stateList?.data?.map((item) => ({
        id: item?.id,
        label: item?.state_name,
        value: item?.state_name,
    })) || [];

    const initialValues = {
        country_id: initialCity?.country_id || '',
        state_id: initialCity?.state_id || '',
        city_name: initialCity?.city_name || '',
    }

    useEffect(() => {
        if (initialCity?.country_id) {
            setCountryId(initialCity.country_id);
        }
    }, [initialCity]);

    const validationSchema = Yup.object().shape({
        country_id: Yup.string().required('Country is required'),
        state_id: Yup.string().required('State is required'),
        city_name: Yup.string().required('City name is required'),
    })

    const createCityMutation = useMutation({
        mutationFn: (formData) => api.post("city/create", formData),
    });

    const updateCityMutation = useMutation({
        mutationFn: (formData) => api.put(`city-update/${initialCity?.id}`, formData),
    });

    const handleSubmit = (values, { resetForm }) => {
        if (isEditing) {
            updateCityMutation.mutate(values, {
                onSuccess: (response) => {
                    if (response?.data?.message) {
                        showToaster("City updated successfully!", '', 'success');
                        queryClient.invalidateQueries({ queryKey: ["all-cities-list"] });
                        onSuccess();
                    } else {
                        showToaster('Something went wrong!', '', 'error');
                    }
                },
                onError: (error) => {
                    showToaster(error?.data?.error || "Failed to update city", '', 'error');
                }
            });
        } else {
            createCityMutation.mutate(values, {
                onSuccess: (response) => {
                    if (response?.data?.message) {
                        showToaster("City added successfully!", '', 'success')
                        resetForm();
                        queryClient.invalidateQueries({ queryKey: ["all-cities-list"] });
                    } else {
                        showToaster('Something went wrong!', '', 'error')
                    }
                },
                onError: (error) => {
                    showToaster(error?.data?.error || "Failed to add city", '', 'error')
                }
            });
        }
    }

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
            {({ resetForm }) => (
                <Form>
                    <Grid2 container spacing={"20px"}>
                        <Grid2 children size={4}>
                            <Field
                                name="city_name"
                                component={TextFormField}
                                placeholder="Enter City Name"
                                label="City Name"
                                isRequired={true}
                            />
                        </Grid2>
                        <Grid2 children size={4}>
                            <Field
                                name="country_id"
                                component={IdAutoCompleteField}
                                options={countryOption}
                                label="Select Country"

                                isRequired={true}
                                isLoading={countryLoading}
                                onValueChange={(value) => {
                                    setCountryId(value?.id);
                                }}
                            />
                        </Grid2>
                        <Grid2 children size={4}>
                            <Field
                                name="state_id"
                                component={IdAutoCompleteField}
                                options={stateOption}
                                label="Select State"
                                isRequired={true}
                                isLoading={stateLoading}
                                onSearchChange={(val) => {
                                    setSearchState(val)
                                }}
                            />
                        </Grid2>
                    </Grid2>
                    <CustomDivider />
                    <Grid2 container spacing={Grid_Gap} sx={{ display: "flex", justifyContent: "end" }}>
                        <Grid2 children size={{ xs: 12, sm: 3 }}
                            onClick={() => {
                                resetForm();
                                if (isEditing) {
                                    onSuccess();
                                }
                            }}
                        >
                            <Button variant='outlined' fullWidth onClick={resetForm}>
                                {isEditing ? "Cancel" : "Reset"}
                            </Button>
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 3 }}
                            disabled={createCityMutation.isLoading || updateCityMutation.isLoading}
                        >
                            <Button variant='contained' type="submit" fullWidth>
                                {isEditing ? "Update" : "Submit"}
                            </Button>
                        </Grid2>
                    </Grid2>
                </Form>
            )}
        </Formik>
    )
}

export default CityForm